/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 페이지 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/page/Page.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 8. 5.
 */
import * as parent from '@components/form/field/Field';
import { Remote } from '@models/store/list/Remote';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Scroller } from '@utils/Scroller';
import './Page.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 페이지목록을 불러올 URL
     */
    url?: string;

    /**
     * 사이트테마에서 페이지를 검색하기 위한 도메인호스트명
     */
    host: string;

    /**
     * 사이트테마에서 페이지를 검색하기 위한 사이트언어코드
     */
    language: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Page extends parent.Field {
    /**
     * 기본 URL
     */
    static url: string = null;

    /**
     * 컴포넌트 확장타입
     */
    variant = 'page';

    /**
     * 목록을 불러올 URL
     */
    url: string;

    /**
     * 도메인호스트명
     */
    host: string;

    /**
     * 사이트언어코드
     */
    language: string;

    /**
     * 페이지목록 DOM
     */
    $pages: Dom = null;

    /**
     * 스크롤러
     */
    scroller: Scroller = null;

    /**
     * 데이터스토어
     */
    store: Remote = null;

    /**
     * iframe 스케일 조정을 위한 ResizeObserver 목록
     */
    resizeObservers: ResizeObserver[] = [];

    /**
     * Page 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Page> = null) {
        super(properties);

        this.url = properties.url ?? Page.url ?? '/';
        this.host = properties.host;
        this.language = properties.language;
    }

    /**
     * 기본 URL 을 설정한다.
     *
     * @param url - 기본 URL
     */
    static setUrl(url: string): void {
        Page.url = url;
    }

    /**
     * 페이지목록 DOM 을 가져온다.
     *
     * @returns $pages
     */
    $getPages(): Dom {
        if (this.$pages === null) {
            this.$pages = Html.create('ul', { 'data-role': 'pages' });
        }

        return this.$pages;
    }

    /**
     * 스크롤러를 가져온다.
     *
     * @returns scroller
     */
    getScroller(): Scroller {
        if (this.scroller === null) {
            this.scroller = Scroller.get({
                $target: this.$getPages(),
                $component: this.$component,
                scrollable: 'x',
            });
        }

        return this.scroller;
    }

    /**
     * 데이터스토어를 가져온다.
     *
     * @returns store
     */
    getStore(): Remote {
        if (this.store === null) {
            this.store = new Remote({
                provider: {
                    url: this.url,
                    params: {
                        host: this.host,
                        language: this.language,
                    },
                },
                listeners: {
                    beforeLoad: () => {
                        this.onBeforeLoad();
                    },
                    load: () => {
                        this.onUpdate();
                    },
                    update: () => {
                        this.onUpdate();
                    },
                },
            });
            this.store.retain();
        }

        return this.store;
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.store?.release();

        await super.remove(removeDom);
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: string | null, is_init: boolean = false): Promise<void> {
        await this.lockValue();

        if (this.getStore().isLoaded() === false) {
            await this.getStore().load();
        }

        const record = await this.getStore().find({ name: value });
        const resolved = record !== null ? record.get('name') : null;

        if (resolved !== null) {
            Html.all('li', this.$getPages()).forEach(($item) => {
                if ($item.getData('name') === resolved) {
                    $item.addClass('selected');
                } else {
                    $item.removeClass('selected');
                }
            });
        } else {
            Html.all('li', this.$getPages()).forEach(($item) => {
                $item.removeClass('selected');
            });
        }

        await this.releaseValue(resolved, is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<string | null> {
        return (await super.getValue()) ?? null;
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.$getPages());

        if (this.getStore().isLoaded() === false) {
            await this.getStore().load();
        }

        this.getScroller().render();
    }

    /**
     * 스토어 데이터 로딩 직전 이벤트를 처리한다.
     */
    onBeforeLoad(): void {
        for (const observer of this.resizeObservers) {
            observer.disconnect();
        }
        this.resizeObservers = [];
        this.$getPages().empty();
    }

    /**
     * 스토어 데이터가 갱신되었을 때 이벤트를 처리한다.
     */
    onUpdate(): void {
        const $pages = this.$getPages();
        $pages.empty();

        for (const record of this.getStore().getRecords()) {
            const $page = Html.create('li', { 'data-name': record.get('name') });

            const $frame = Html.create('div', { 'data-role': 'iframe' });
            const $iframe = Html.create('iframe', { src: record.get('preview'), tabindex: '-1' });

            $frame.append($iframe);
            $page.append($frame);

            const $name = Html.create('b', null, record.get('name') as string);
            $page.append($name);

            const $button = Html.create('button', { type: 'button', 'data-name': record.get('name') as string });
            $button.on('click', () => {
                this.setValue($button.getData('name') as string);
            });
            $page.append($button);

            $pages.append($page);

            const applyScale = () => {
                const frameEl = $frame.getEl();
                const frameWidth = frameEl.clientWidth;
                const frameHeight = frameEl.clientHeight;
                if (frameWidth > 0) {
                    const scale = frameWidth / 1280;
                    const iframeHeight = Math.round(frameHeight / scale);
                    $iframe.setStyle('width', '1280px');
                    $iframe.setStyle('height', `${iframeHeight}px`);
                    $iframe.setStyle('transform', `scale(${scale})`);
                }
            };

            const observer = new ResizeObserver(applyScale);
            observer.observe($frame.getEl());
            this.resizeObservers.push(observer);
        }

        const currentValue = this.properties.value ?? null;
        if (currentValue !== null) {
            this.setValue(currentValue as string);
        }
    }
}
